/**
 * AccountPauseSection - Take a break section for pausing account.
 * Shows an amber-bordered section with a pause button that opens a confirmation modal.
 * Supports password verification for password-based accounts and
 * OAuth re-authentication note for OAuth-only users.
 */

import { useAuth } from '@contexts/AuthContext';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, PauseCircle, X } from 'lucide-react';
import type { ReactElement } from 'react';
import { useState } from 'react';

export function AccountPauseSection(): ReactElement {
  const { user, pauseAccount } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const hasPassword = user?.hasPassword === true;

  const handlePause = async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      await pauseAccount(hasPassword ? password : undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to pause account');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = (): void => {
    if (loading) return;
    setShowModal(false);
    setPassword('');
    setError('');
  };

  const isConfirmDisabled = loading || (hasPassword && !password);

  return (
    <section className="mb-6">
      <div className="bg-theme-surface rounded-lg p-4 border border-amber-500/30">
        <div className="flex items-center gap-2 mb-2">
          <PauseCircle className="w-4 h-4 text-amber-500" />
          <h3 className="text-lg font-medium text-amber-500">Take a Break</h3>
        </div>
        <p className="text-sm text-theme-text-secondary mb-4">
          Temporarily pause your account. Your data is preserved and your subscription billing is
          paused. You can resume anytime by logging in.
        </p>
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <PauseCircle className="w-4 h-4" />
          Pause Account
        </button>
      </div>

      {/* Pause Confirmation Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute inset-0 bg-black/50"
              onClick={handleCloseModal}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="relative bg-theme-surface border border-theme-border rounded-xl shadow-2xl max-w-md w-full p-6"
            >
              {/* Close button */}
              <button
                type="button"
                onClick={handleCloseModal}
                disabled={loading}
                className="absolute top-3 right-3 p-1 rounded-md text-theme-text-tertiary hover:text-theme-text-secondary hover:bg-theme-surface-hover transition disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center bg-amber-600/15">
                  <PauseCircle className="w-8 h-8 text-amber-500" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold text-center mb-2 text-amber-500">Pause Account</h3>

              {/* Warning */}
              <div className="text-sm text-theme-text-secondary space-y-2 mb-5">
                <p>This will temporarily pause your account. Please note:</p>
                <ul className="list-disc list-inside space-y-1 text-theme-text-tertiary">
                  <li>
                    All your picks, parlays, and analysis history will be{' '}
                    <span className="text-theme-text font-medium">preserved</span>
                  </li>
                  <li>Active subscription billing will be paused (not cancelled)</li>
                  <li>You will be logged out while your account is paused</li>
                  <li>Log in anytime to resume your account</li>
                </ul>
              </div>

              {/* Password input for password-based users */}
              {hasPassword ? (
                <div className="mb-4">
                  <label
                    htmlFor="pause-account-password"
                    className="block text-xs text-theme-text-tertiary mb-1"
                  >
                    Enter your password to confirm
                  </label>
                  <input
                    id="pause-account-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your current password"
                    disabled={loading}
                    className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-theme-text text-sm focus:outline-none focus:border-amber-500 disabled:opacity-50"
                  />
                </div>
              ) : (
                <div className="mb-4 p-3 bg-theme-bg rounded-lg border border-theme-border">
                  <p className="text-xs text-theme-text-tertiary">
                    Your account uses social sign-in (OAuth). Clicking pause will immediately pause
                    your account. Log in anytime to resume.
                  </p>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="mb-4 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-amber-500 text-sm">
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={loading}
                  className="flex-1 py-2.5 rounded-lg text-theme-text-secondary hover:bg-theme-surface-hover font-medium transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePause}
                  disabled={isConfirmDisabled}
                  className="flex-1 py-2.5 rounded-lg font-medium transition bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Pausing...
                    </>
                  ) : (
                    'Pause My Account'
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
}
